import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import type { DateTime } from "luxon";
import { AppUserInfoContext } from "@/shared/model/user/AppUserInfoContext.ts";
import {
  calculateMarginPercentage,
  calculatePriceWithoutTax,
  calculateTotalCostPriceForOneUnit,
} from "@/pages/management/technicalsheets/technicalsheet/components/TechnicalSheetCalculations.ts";
import type {
  Component,
  TechnicalSheet,
} from "@/pages/management/technicalsheets/technicalsheet/services/dto/TechnicalSheet.ts";
import type { FinancialData } from "@/pages/management/technicalsheets/technicalsheet/services/dto/FinancialData.ts";
import { getTechnicalSheetData } from "@/pages/management/technicalsheets/technicalsheet/services/TechnicalSheetApi.ts";
import { getFinancialData } from "@/pages/management/technicalsheets/technicalsheet/services/FinancialDataApi.ts";
import { CostPriceType } from "@/pages/management/technicalsheets/technicalsheet/components/CostPriceType.ts";
import type { Unit } from "@/pages/management/technicalsheets/technicalsheet/services/dto/Unit.ts";
import { getUnits } from "@/pages/management/technicalsheets/technicalsheet/services/UnitsApi.ts";
import { checkDateTime } from "@/shared/utils/Dates.ts";

export interface TechnicalSheetContextProps {
  technicalSheetData: TechnicalSheet | undefined;
  fetchTechnicalSheetData: (
    technicalSheetId: string,
    domainId: string,
  ) => Promise<void>;
  fetchTechnicalSheetDataWithoutRefresh: (
    technicalSheetId: string,
    domainId: string,
  ) => Promise<void>;
  financialData: FinancialData | undefined;
  fetchFinancialData: (
    domainId: string,
    siteId: string,
    date: DateTime,
    valuationId: string,
    componentIds: string[],
  ) => Promise<void>;
  units: Unit[];
  fetchUnits: (domainId: string) => Promise<void>;
  updateFieldWithUserInfo: (
    field: keyof TechnicalSheet,
    value: number | string | boolean | Component[] | undefined,
  ) => void;
  minThreshold: number;
  maxThreshold: number;
  setThresholds: (min: number, max: number) => void;
  multiplier: number;
  setMultiplier: (multiplier: number) => void;
}

export const TechnicalSheetContext = createContext<
  TechnicalSheetContextProps | undefined
>(undefined);

export const TechnicalSheetDataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}: {
  children: ReactNode;
}) => {
  const appUserInfo = useContext(AppUserInfoContext);
  const [technicalSheetData, setTechnicalSheetData] = useState<
    TechnicalSheet | undefined
  >(undefined);
  const [financialData, setFinancialData] = useState<FinancialData | undefined>(
    undefined,
  );
  const [units, setUnits] = useState<Unit[] | []>([]);
  const [minThreshold, setMinThreshold] = useState<number>(35);
  const [maxThreshold, setMaxThreshold] = useState<number>(65);
  const [multiplier, setMultiplier] = useState<number>(10);

  const recalculateComputedFields = (
    tsData: TechnicalSheet,
    fData?: FinancialData,
  ): TechnicalSheet => {
    const producedQuantity = tsData.producedQuantity ?? 1;
    const totalCostPrice =
      fData ?
        calculateTotalCostPriceForOneUnit(
          tsData.components,
          producedQuantity,
          fData.componentsCostPrices,
        )
      : 0;
    const totalPreviousCostPrice =
      fData ?
        calculateTotalCostPriceForOneUnit(
          tsData.components,
          producedQuantity,
          fData.componentsCostPrices,
          CostPriceType.PREVIOUS,
        )
      : 0;
    const priceWithoutTax = calculatePriceWithoutTax(
      tsData.priceInclTax,
      tsData.vatrate,
    );
    const marginPercentage = calculateMarginPercentage(
      priceWithoutTax,
      totalCostPrice,
    );

    return {
      ...tsData,
      totalCostPrice,
      totalPreviousCostPrice,
      priceWithoutTax,
      marginPercentage,
    };
  };

  useEffect(() => {
    if (technicalSheetData) {
      const updatedData = recalculateComputedFields(
        technicalSheetData,
        financialData,
      );
      setTechnicalSheetData((prev) =>
        JSON.stringify(prev) !== JSON.stringify(updatedData) ?
          updatedData
        : prev,
      );
    }
  }, [technicalSheetData, financialData]);

  const fetchTechnicalSheetData = async (
    technicalSheetId: string,
    domainId: string,
  ): Promise<void> => {
    setTechnicalSheetData(undefined);
    setFinancialData(undefined);
    const data = await getTechnicalSheetData(technicalSheetId, domainId);
    setTechnicalSheetData(data);
  };

  const fetchTechnicalSheetDataWithoutRefresh = async (
    technicalSheetId: string,
    domainId: string,
  ): Promise<void> => {
    const data = await getTechnicalSheetData(technicalSheetId, domainId);
    setTechnicalSheetData(data);
  };

  const fetchFinancialData = async (
    domainId: string,
    siteId: string,
    date: DateTime,
    valuationId: string,
    componentIds: string[],
  ): Promise<void> => {
    setFinancialData(undefined);
    const financialData = await getFinancialData(
      domainId,
      siteId,
      checkDateTime(date).toISO(),
      valuationId,
      componentIds,
    );
    setFinancialData(financialData);
  };

  const fetchUnits = async (domainId: string): Promise<void> => {
    setUnits([]);
    const units = await getUnits(domainId);
    setUnits(units);
  };

  const updateFieldWithUserInfo = (
    field: keyof TechnicalSheet,
    value: number | string | boolean | Component[] | undefined,
  ): void => {
    setTechnicalSheetData((prev) => {
      if (!prev) return undefined;

      const updatedData = {
        ...prev,
        [field]: value,
        userMName: appUserInfo.userInfo?.fullName() ?? "",
        dateM: new Date().toISOString(),
      };

      return financialData ?
          recalculateComputedFields(updatedData, financialData)
        : updatedData;
    });
  };

  const setThresholds = (min: number, max: number): void => {
    setMinThreshold(min);
    setMaxThreshold(max);
  };

  return (
    <TechnicalSheetContext.Provider
      value={{
        technicalSheetData,
        fetchTechnicalSheetData,
        fetchTechnicalSheetDataWithoutRefresh,
        financialData,
        fetchFinancialData,
        units,
        fetchUnits,
        updateFieldWithUserInfo,
        minThreshold,
        maxThreshold,
        setThresholds,
        multiplier,
        setMultiplier,
      }}
    >
      {children}
    </TechnicalSheetContext.Provider>
  );
};
